import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Compass } from "lucide-react";

import { classNames } from "@/utils/classNames";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import {
	openTour,
	selectTourState,
} from "@/pages/Private/redux/onboardingTour/onboardingTour.slice";
import { OnboardingTourStatus } from "@/pages/Private/redux/onboardingTour/schema";
import {
	isTourStepComplete,
	visibleTourSteps,
} from "@/pages/Private/pages/OnboardingTour/tourSteps";

interface SidebarSetupGuideProps {
	isExpanded: boolean;
	/** The footer's shared row styling, so this row lines up with its siblings. */
	rowClass: string;
}

/**
 * Permanent way back into the setup guide (PREC-3861). Every other entry point
 * is conditional or dismissible — the dashboard banner and the ready bar both
 * hide for the session, and both live on surfaces the user may not return to —
 * so someone who skipped the guide had no way back to, say, "Check & activate"
 * and reasonably concluded the screen did not exist.
 *
 * This row sits in the sidebar on every app surface and stays until the guide is
 * actually finished. It reopens the tour where the user left off.
 */
export const SidebarSetupGuide: FunctionComponent<SidebarSetupGuideProps> = ({
	isExpanded,
	rowClass,
}) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const tourState = useAppSelector(selectTourState);
	const { user } = useAppSelector(authSelector);
	const extensionPromoEnabled = useIsFeatureActive(FeaturesCodes.EXTENSION_PROMOTION) === true;

	if (!tourState || !user?.isOnboarded || tourState.status === OnboardingTourStatus.DONE) {
		return null;
	}

	const remaining = visibleTourSteps(
		tourState,
		Boolean(user?.linkedinAllowed),
		extensionPromoEnabled
	).filter((step) => !isTourStepComplete(tourState, step)).length;

	if (remaining === 0) {
		return null;
	}

	return (
		<button
			className={classNames(rowClass, "text-brand-light")}
			type="button"
			onClick={() => dispatch(openTour(undefined))}
		>
			{/* Collapsed rail: the label lives in the tooltip, as on every other row. */}
			<Tooltip
				content={t("onboardingTour.sidebar.title")}
				direction="top-right"
				disabled={isExpanded}
				variant="dark"
			>
				<Compass className="h-[19px] w-[19px] flex-shrink-0" strokeWidth={1.9} />
			</Tooltip>
			{isExpanded && (
				<span className="whitespace-nowrap text-sm font-semibold">
					{t("onboardingTour.sidebar.title")}
					<span className="ml-1 text-xs font-normal text-text-meta">
						({t("onboardingTour.sidebar.remaining", { count: remaining })})
					</span>
				</span>
			)}
		</button>
	);
};
